import { Toggle } from '@fluentui/react';
import { getDate, Item, itemStylesCategory, toggleStylesLabelOnly, useClassNames } from '@h2oai/ui-kit';
import { ReactElement } from 'react';

import { FeatureSet } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import {
  featureSetMetaDataFieldsStylesDefault,
  IFeatureSetMetaDataFieldsClassNames,
  IFeatureSetMetaDataFieldsStyles,
} from './FeatureSetMetaDataFields.styles';

interface FieldGroup {
  title: string;
  data: any[][];
  isTag?: boolean;
}

const getFieldGroups = (data: FeatureSet): FieldGroup[] => {
  const fieldGroups = [];
  const { timeToLive, specialData, timeTravelScope, timeTravelColumnFormat, tags } = data;
  if (timeToLive) {
    fieldGroups.push({
      title: 'Time To Live',
      data: [
        ['Offline', `${timeToLive.ttlOffline} ${timeToLive.ttlOfflineInterval?.toLowerCase()}`],
        ['Online', `${timeToLive.ttlOnline} ${timeToLive.ttlOnlineInterval?.toLowerCase()}`],
      ],
    });
  }
  if (specialData) {
    fieldGroups.push({
      title: 'Special Items',
      data: [
        [
          'SPI',
          <Toggle
            key="spi"
            label={specialData.spi ? 'True' : 'False'}
            inlineLabel
            checked={specialData.spi}
            // TODO: when it can be editable, it will be editable.
            styles={toggleStylesLabelOnly}
          />,
        ],
        [
          'PCI',
          <Toggle
            key="pci"
            label={specialData.pci ? 'True' : 'False'}
            inlineLabel
            checked={specialData.pci}
            // TODO: when it can be editable, it will be editable.
            styles={toggleStylesLabelOnly}
          />,
        ],
        [
          'RPI',
          <Toggle
            key="rpi"
            label={specialData.rpi ? 'True' : 'False'}
            inlineLabel
            checked={specialData.rpi}
            // TODO: when it can be editable, it will be editable.
            styles={toggleStylesLabelOnly}
          />,
        ],
        [
          'Demographic',
          <Toggle
            key="demographic"
            label={specialData.demographic ? 'True' : 'False'}
            inlineLabel
            checked={specialData.demographic}
            // TODO: when it can be editable, it will be editable.
            styles={toggleStylesLabelOnly}
          />,
        ],
        [
          'Sensitive',
          <Toggle
            key="Sensitive"
            label={specialData.sensitive ? 'True' : 'False'}
            inlineLabel
            checked={specialData.sensitive}
            // TODO: when it can be editable, it will be editable.
            styles={toggleStylesLabelOnly}
          />,
        ],
      ],
    });
  }
  if (timeTravelScope) {
    fieldGroups.push({
      title: 'Time Travel',
      data: [
        ['Format', timeTravelColumnFormat],
        ['', ''],
        ['Start Date', `${getDate(timeTravelScope.startDateTime)?.toLocaleString()}`],
        ['End Date', `${getDate(timeTravelScope.endDateTime)?.toLocaleString()}`],
      ],
    });
  }
  if (tags) {
    fieldGroups.push({
      title: 'Tags',
      isTag: true,
      data: tags.map((tag) => [tag]),
    });
  }
  return fieldGroups;
};

const renderFields = (classNames: IFeatureSetMetaDataFieldsClassNames, fieldGroup: FieldGroup) => {
  const { data, title, isTag } = fieldGroup;
  const items: ReactElement[][] = [];
  const lastIndex = data.length - 1;
  return (
    <div className={`${classNames.fieldGroup}${isTag ? ' is-tag' : ''}`}>
      {data.length ? (
        data.map((item, i) => {
          if (isTag) {
            return (
              <Item
                key={item[0]}
                styles={[itemStylesCategory, { root: { marginRight: 16, marginBottom: 16 } }]}
                labelField="label"
                titleField="title"
                data={{ id: item[0], label: item[0], title: item[0] }}
              />
            );
          } else {
            items.push(item);
            if (items.length === 2 || i === lastIndex) {
              const renderItems = items.splice(0, items.length);
              return (
                <div key={i} className={classNames.fields}>
                  {renderItems.map((item, j) => (
                    <div key={`${i}-${j}`} className={classNames.field}>
                      <div className={classNames.label}>{item[0]}</div>
                      <div className={classNames.value}>{item[1]}</div>
                    </div>
                  ))}
                </div>
              );
            }
            return null;
          }
        })
      ) : (
        <div className={classNames.emptyFieldGroup}>No {title} Available</div>
      )}
    </div>
  );
};

export interface IFeatureSetMetaDataFieldsProps {
  data: FeatureSet;
}

export const FeatureSetMetaDataFields = ({ data }: IFeatureSetMetaDataFieldsProps) => {
  const classNames = useClassNames<IFeatureSetMetaDataFieldsStyles, IFeatureSetMetaDataFieldsClassNames>(
    'FeatureSetMetaDataFields',
    featureSetMetaDataFieldsStylesDefault
  );
  return (
    <div className={classNames.root} data-test="feature-set-key-details">
      {getFieldGroups(data).map((d, i) => (
        <div className={classNames.panel} key={d.title}>
          <div className={`${classNames.title} panel-${i + 1}`}>{d.title}</div>
          {renderFields(classNames, d)}
        </div>
      ))}
    </div>
  );
};
